(function (root) {
  'use strict';

  var COPPER_RESISTIVITY_OHM_MM2_PER_M = 0.0172;
  var STANDARD_CABLE_SIZES_MM2 = Object.freeze([1.5, 2.5, 4, 6, 10, 16, 25, 35, 50, 70, 95]);

  function toFiniteNumber(value) {
    if (typeof value === 'string') {
      value = value.trim().replace(',', '.');
    }

    var number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function calculateCableSize(input) {
    input = input || {};

    var voltage = toFiniteNumber(input.voltage);
    var current = toFiniteNumber(input.current);
    var length = toFiniteNumber(input.length);
    var voltageDropPercent = toFiniteNumber(
      input.voltageDropPercent == null ? 3 : input.voltageDropPercent
    );
    var lengthMode = input.lengthMode === 'round-trip' ? 'round-trip' : 'one-way';

    if (voltage !== 12 && voltage !== 24) {
      return { valid: false, error: 'voltage' };
    }

    if (current == null || current <= 0) {
      return { valid: false, error: 'current' };
    }

    if (length == null || length <= 0) {
      return { valid: false, error: 'length' };
    }

    if (voltageDropPercent == null || voltageDropPercent <= 0 || voltageDropPercent >= 100) {
      return { valid: false, error: 'voltage-drop' };
    }

    var roundTripLength = lengthMode === 'round-trip' ? length : length * 2;
    var allowedVoltageDrop = voltage * (voltageDropPercent / 100);
    var theoreticalSize =
      (current * COPPER_RESISTIVITY_OHM_MM2_PER_M * roundTripLength) / allowedVoltageDrop;

    if (!Number.isFinite(theoreticalSize) || theoreticalSize <= 0) {
      return { valid: false, error: 'calculation' };
    }

    var recommendedSize =
      STANDARD_CABLE_SIZES_MM2.find(function (size) {
        var floatingPointTolerance = Math.max(1, size) * Number.EPSILON * 16;
        return theoreticalSize <= size + floatingPointTolerance;
      }) || null;

    var actualVoltageDrop = null;
    var actualVoltageDropPercent = null;

    if (recommendedSize != null) {
      actualVoltageDrop =
        (current * COPPER_RESISTIVITY_OHM_MM2_PER_M * roundTripLength) / recommendedSize;
      actualVoltageDropPercent = (actualVoltageDrop / voltage) * 100;
    }

    return {
      valid: true,
      voltage: voltage,
      current: current,
      enteredLength: length,
      lengthMode: lengthMode,
      roundTripLength: roundTripLength,
      voltageDropPercent: voltageDropPercent,
      allowedVoltageDrop: allowedVoltageDrop,
      theoreticalSize: theoreticalSize,
      recommendedSize: recommendedSize,
      actualVoltageDrop: actualVoltageDrop,
      actualVoltageDropPercent: actualVoltageDropPercent,
      exceedsAvailableSizes: recommendedSize == null
    };
  }

  var api = Object.freeze({
    COPPER_RESISTIVITY_OHM_MM2_PER_M: COPPER_RESISTIVITY_OHM_MM2_PER_M,
    STANDARD_CABLE_SIZES_MM2: STANDARD_CABLE_SIZES_MM2,
    calculateCableSize: calculateCableSize
  });

  root.PVCableSizeCalculator = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (
    typeof HTMLElement === 'undefined' ||
    typeof customElements === 'undefined' ||
    customElements.get('pv-cable-size-calculator')
  ) {
    return;
  }

  function parseProductMap(element) {
    var node = element.querySelector('[data-product-map]');
    if (!node) return {};

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (_error) {
      return {};
    }
  }

  class PVCableSizeCalculatorElement extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = this.querySelector('[data-calculator-form]');
      this.errorNode = this.querySelector('[data-calculator-error]');
      this.resultsNode = this.querySelector('[data-calculator-results]');
      this.theoreticalNode = this.querySelector('[data-theoretical-size]');
      this.recommendedNode = this.querySelector('[data-recommended-size]');
      this.dropNode = this.querySelector('[data-voltage-drop]');
      this.roundTripNode = this.querySelector('[data-round-trip-length]');
      this.productLink = this.querySelector('[data-product-link]');
      this.contactLink = this.querySelector('[data-contact-link]');
      this.productMap = parseProductMap(this);
      this.hasCalculated = false;

      if (!this.form) return;

      this.form.addEventListener('submit', this.handleSubmit.bind(this));
      this.form.addEventListener('input', this.handleInput.bind(this));
      this.form.addEventListener('reset', this.handleReset.bind(this));
    }

    handleSubmit(event) {
      event.preventDefault();
      this.hasCalculated = true;
      this.renderCalculation();
    }

    handleInput() {
      if (this.hasCalculated) this.renderCalculation();
    }

    handleReset() {
      var element = this;
      root.setTimeout(function () {
        element.hasCalculated = false;
        element.hideError();
        element.resultsNode.hidden = true;
      }, 0);
    }

    readInput() {
      var elements = this.form.elements;
      return {
        voltage: elements.namedItem('voltage').value,
        current: elements.namedItem('current').value,
        length: elements.namedItem('length').value,
        lengthMode: elements.namedItem('length_mode').value,
        voltageDropPercent: 3
      };
    }

    renderCalculation() {
      var result = calculateCableSize(this.readInput());

      if (!result.valid) {
        this.resultsNode.hidden = true;
        this.showError(this.dataset.invalidMessage || 'Enter valid values greater than zero.');
        return;
      }

      this.hideError();
      this.resultsNode.hidden = false;
      this.theoreticalNode.textContent = this.formatNumber(result.theoreticalSize, 2) + ' mm²';
      this.roundTripNode.textContent = this.formatNumber(result.roundTripLength, 2) + ' m';

      if (result.exceedsAvailableSizes) {
        this.recommendedNode.textContent =
          this.dataset.tooLargeMessage || 'Larger than the available standard sizes';
        this.dropNode.textContent = '—';
        this.productLink.hidden = true;
        this.contactLink.hidden = false;
        return;
      }

      var sizeLabel = this.formatCableSize(result.recommendedSize);
      this.recommendedNode.textContent = sizeLabel + ' mm²';
      this.dropNode.textContent =
        this.formatNumber(result.actualVoltageDrop, 2) +
        ' V (' +
        this.formatNumber(result.actualVoltageDropPercent, 2) +
        ' %)';

      this.contactLink.hidden = true;
      this.configureProductLink(result.recommendedSize, sizeLabel);
    }

    configureProductLink(size, sizeLabel) {
      var mapKey = String(size);
      var configuredUrl = this.productMap[mapKey];
      var targetUrl;

      if (configuredUrl) {
        targetUrl = configuredUrl;
      } else {
        var searchUrl = new URL(this.dataset.searchUrl || '/search', root.location.origin);
        var queryTemplate = this.dataset.searchTermTemplate || 'batterikabel {size} mm2';
        searchUrl.searchParams.set('q', queryTemplate.replace('{size}', sizeLabel));
        searchUrl.searchParams.set('type', 'product');
        searchUrl.searchParams.set('options[prefix]', 'last');
        targetUrl = searchUrl.toString();
      }

      var labelTemplate = this.dataset.productLinkTemplate || 'Find {size} mm² cable';
      this.productLink.href = targetUrl;
      this.productLink.textContent = labelTemplate.replace('{size}', sizeLabel);
      this.productLink.hidden = false;
    }

    showError(message) {
      this.errorNode.textContent = message;
      this.errorNode.hidden = false;
    }

    hideError() {
      this.errorNode.textContent = '';
      this.errorNode.hidden = true;
    }

    formatCableSize(value) {
      return this.formatNumber(value, Number.isInteger(value) ? 0 : 1);
    }

    formatNumber(value, maximumFractionDigits) {
      return new Intl.NumberFormat(this.dataset.locale || 'nb-NO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: maximumFractionDigits
      }).format(value);
    }
  }

  customElements.define('pv-cable-size-calculator', PVCableSizeCalculatorElement);
})(typeof window !== 'undefined' ? window : globalThis);
